/**
 * The paths a signed-out visitor is allowed to be on.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * This list used to exist twice. `routes.jsx` declared 26 public routes, and
 * `api/client.js` carried its own hand-written copy of six path prefixes to
 * decide whether a 401 meant "your session died, go and log in" or "you are a
 * guest, that endpoint simply is not yours".
 *
 * The copies disagreed, so every public page NOT in the shorter list — the
 * reviewer directory, pricing, about, help, public profiles, shared reports —
 * treated a routine guest 401 as an expired session and threw the visitor at
 * `/login?reason=session_expired`. `/reviewers` carries a comment in
 * `routes.jsx` explaining that it must never bounce a guest, "the one visitor
 * the page exists for", and it bounced them anyway: the guard it was protected
 * by lived in a different file and had never heard of it.
 *
 * The message was also false. A first-time visitor with no session was told
 * their session had expired.
 *
 * So the list lives here once, and both sides read it. `routes.jsx` builds its
 * public routing table from PUBLIC_ROUTE_PATHS, and `client.js` asks
 * `isPublicPath()`. `publicPaths.test.js` fails if a public route is ever
 * declared that this file does not know about.
 */

/**
 * Every path pattern reachable without a session, in React Router syntax.
 * `:param` matches one segment; a trailing `*` matches the rest.
 *
 * Adding a public route? Add it here too — the unit test enforces it.
 */
export const PUBLIC_ROUTE_PATHS = [
  '/',

  // Guest interview + the report it produces.
  '/guest/requirements/:id',
  '/guest/requirements/v2/:id',
  '/guest/requirements/v2/:id/report',
  '/guest/requirements/v2/:id/reviews',
  '/guest/requirements/v2/:id/project',

  // Links that ARE the credential. Each arrives in an email, and the token in
  // the path is what authorises the visit — so demanding a session first
  // bounces the one person the link was sent to. An invited reviewer has no
  // account yet by definition.
  '/invite/:token',
  '/proposals/claim/:token',
  // RF-087: the one-click unsubscribe link in a cold-outreach proposal
  // email. The recipient has no session at all, not even a guest one — the
  // token in the path is the whole authorization, same as the claim link.
  '/proposals/unsubscribe/:token',

  // Shared / previewable work.
  '/p/:slug',
  '/projects/:id',
  '/projects/:id/preview',
  '/projects/:id/preview/:previewType',
  '/examples/:exampleCode',
  // Token-bearing landings: an invite and a proposal claim. Declared public
  // in routes.jsx; a 401 on them is a bad token, not an expired session.
  '/invite/:token',
  '/proposals/claim/:token',

  // Public directories and profiles.
  '/reviewers',
  '/people/:id',
  '/organisations/:id',
  '/organisations/slug/:slug',

  // Marketing.
  '/about',
  '/pricing',
  '/integrations',
  '/integrations/:slug',
  '/help',
  '/search',
  '/become-a-reviewer',
  // One onboarding per role × level, before any account.
  '/become-a-reviewer/start',
  '/reviewer-pricing',

  // Auth front doors. A 401 here is the normal state, not an expiry.
  '/login',
  '/register',
  '/auth/callback/:provider',
  '/password-reset/request',
  '/password-reset/confirm',
  '/verify-email',

  '/error/500'
];

/**
 * Content-managed pages are served by the catch-all from the dictionary
 * rather than declared as routes, so they cannot be enumerated above.
 * They are public by definition.
 */
const PUBLIC_PREFIXES = ['/legal/'];

/** `/organisations/slug/:slug` → /^\/organisations\/slug\/[^/]+$/ */
const toRegExp = (pattern) => new RegExp(
  `^${pattern
    .split('/')
    .map((seg) => {
      if (seg === '*') return '.*';
      if (seg.startsWith(':')) return '[^/]+';
      return seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/')}/?$`
);

const MATCHERS = PUBLIC_ROUTE_PATHS.map(toRegExp);

/**
 * True when a signed-out visitor is entitled to be on `pathname`, and so a
 * 401 from it must NOT be read as an expired session.
 *
 * Defaults to false: an unknown path is treated as private, so the failure
 * mode of forgetting to register a route is a redundant login prompt on that
 * one page, never a private page silently swallowing its own auth errors.
 */
export function isPublicPath(pathname) {
  // No path is not the same as the root path. `location.pathname` is always at
  // least '/', so an empty value means the caller has nothing to judge — and
  // an unknown location must fall to private, not to the one public page whose
  // pattern an empty string would otherwise collapse into.
  if (typeof pathname !== 'string' || pathname === '') return false;
  const path = pathname.split('?')[0].split('#')[0];
  if (path === '') return false;
  if (PUBLIC_PREFIXES.some((p) => path.startsWith(p))) return true;
  return MATCHERS.some((re) => re.test(path));
}

export default isPublicPath;
